/** DSD 프리뷰 렌더러: parsedContent 기반 React 컴포넌트 렌더링 */

import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/utils";
import { ATTR } from "@/utils/dom-attrs";
import { roundedDiffLabel } from "@/utils/ixd-dsd-compare/rounded-diff-label";
import { periodOfCellId } from "@/utils/ixd-dsd-compare/cell-id-selector";
import { groupByAnchor } from "@/utils/ixd-dsd-compare/header-counterpart";
import {
  POSITION_CARD_BOUNDARY_ATTR,
  POSITION_REVIEW_CONTROL_ATTR,
  PositionCheckMarker,
  PositionCurrentLineProvider,
} from "./position-check/position-check";
import {
  lineKey,
  type PositionCheckSummary,
} from "./position-check/use-position-check";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/styles/ui/tooltip";
import {
  DSDSubChunkInfo,
  DsdCellMatchStatus,
  DsdHeaderTag,
  DsdParagraphItem,
  DsdParsedCell,
  DsdSentenceMatch,
  MatchedIxdCell,
  PeriodType,
} from "@/apis/compare/route";
import { DsdTable, type DsdMatchResult } from "./dsd-table";
import { TableScrollArea } from "@/toggle-ui-kit/table-scroll-area";
import { useDsdCellNavigation } from "./use-dsd-cell-navigation";
import { dsdCellDomId, useDsdCellSelection } from "./use-dsd-cell-selection";
import { selectableCellAttrs } from "@/hooks/use-table-cell-selection";
import { DsdCellText } from "@/utils/dsd-cell-text";
import {
  HeaderTagPopover,
  type ReviewCellKind,
  type TagDimension,
} from "./header-tag-popover";
import { parseCellId, normalizeCellId } from "@/utils/ixd-cell-model";
// 매칭 강조/dim 시각 토큰은 IXD 렌더러(use-ixd-cell-review-styles.ts)와 공유한다.
import {
  DSD_SECTION_SELECTOR,
  applyFocusScroll,
  planFocusScroll,
} from "@/utils/ixd-dsd-compare/focus-utils";
import {
  DL_REVIEW_COLOR_COUNT,
  LINE_ITEM_REVIEW_COLOR_COUNT,
  REVIEW_COUNTERPART_CLASS,
  REVIEW_DIM_CLASS,
  REVIEW_FOCUS_CLASS,
} from "@/components/ixd-preview";

/**
 * 원문 style 중 대사 화면이 살려 쓰는 속성 — 허용목록이다.
 *
 * 대사 화면의 목적은 값 대조이지 원본 레이아웃 재현이 아니다. 금지목록으로 두면
 * 원문이나 변환기가 내는 새 속성이 그대로 셀에 붙어, 증상이 보고된 뒤에야 목록을
 * 고치게 된다 — `height` 가 그렇게 흘러들어 말주석 칸을 수백 px 로 늘렸다.
 * DSD 원본보기(`plain-dsd-table-view.tsx` 의 `PLAIN_DSD_STYLE_PROPS`)도 허용목록이라
 * 같은 원문 style 을 거르는 방식이 두 렌더러에서 어긋나지 않는다.
 *
 * 실측(실파이프라인 변환 HTML, 보고서 3건 td/th 12,192칸): 원문이 싣는 속성은
 * width·height·text-align·vertical-align·font-*·line-height·background-color·
 * text-decoration·padding-left 뿐이다. 이 목록은 그중 대조에 필요한 것만 남기고,
 * 금지목록 시절 대비 추가로 빠지는 속성은 없다(글꼴·배경·테두리는 그때도 빠졌다).
 */
const ALLOWED_CSS_PROPS = new Set([
  "width",
  "min-width",
  "max-width",
  "text-align",
  "vertical-align",
  // 계정 들여쓰기. 실측 4칸뿐이지만 빼면 그 칸들이 들여쓰기를 잃는다.
  "padding-left",
]);

/** CSS 문자열 → React CSSProperties 변환 (허용목록 속성만 남긴다) */
function parseCssStyle(
  css: string | null | undefined,
): React.CSSProperties | undefined {
  if (!css) return undefined;
  const style: Record<string, string> = {};
  for (const decl of css.split(";")) {
    const colon = decl.indexOf(":");
    if (colon === -1) continue;
    const prop = decl.slice(0, colon).trim().toLowerCase();
    const value = decl.slice(colon + 1).trim();
    if (!prop || !value) continue;
    if (!ALLOWED_CSS_PROPS.has(prop)) continue;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    style[camel] = value;
  }
  return Object.keys(style).length > 0 ? style : undefined;
}

// ============================================
// 타입
// ============================================

export interface DsdPreviewRendererHandle {
  scrollToDsdId: (dsdId: string) => void;
  clearFocus: () => void;
  /** DSD 미리보기의 세로 스크롤 영역 */
  getScrollContainer: () => HTMLElement | null;
  /** 화면에 그려진 DSD 칸 */
  getCellElement: (dsdId: string) => HTMLElement | null;
}

/**
 * DSD 표 안에서 하나의 DL 로 배정된 셀 영역.
 */
export interface DsdDlRegionView {
  dlViewId: string;
  /** 그 DL 의 한글 제목. 상위가 IXD preview 에서 찾아 붙인다(못 찾으면 없음). */
  title?: string | null;
  dsdIds: string[];
}

/**
 * DSD 표 안에서 하나의 기간(당기/전기)으로 배정된 셀 영역.
 * ``period`` 는 `CURRENT` | `PREVIOUS` | `BEFORE_PREVIOUS`.
 */
export interface DsdPeriodRegionView {
  period: string;
  dsdIds: string[];
}

/**
 * 영역 목록을 dsdId → 값 맵으로 눕힌다. `valueOf` 가 `undefined` 면 **키를 안 만든다** —
 * 이 파일의 속성 규약(부재 = 미배정)을 맵에서도 유지해 `has()` 로 읽어도 같은 답이 되게.
 */
function indexByDsdId<T extends { dsdIds: string[] }>(
  regions: T[] | null | undefined,
  valueOf: (region: T) => string | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const region of regions ?? []) {
    const value = valueOf(region);
    if (value === undefined) continue;
    for (const dsdId of region.dsdIds) map.set(dsdId, value);
  }
  return map;
}

/**
 * DSD 표 안에서 하나의 line_item 으로 배정된 셀 영역.
 * ``lineItemId`` 는 태그 id — 한글 이름표(``title``)는 상위가 **그 DL 의 표** 에서 찾아
 * 붙인다. 같은 태그의 문구가 표마다 다르기 때문이다(`buildDsdLineItemRegionViews`).
 */
export interface DsdLineItemRegionView {
  lineItemId: string;
  /** 그 항목의 한글 이름표. 상위가 **그 DL 의 표** 에서 찾아 붙인다(못 찾으면 없음). */
  title?: string | null;
  dsdIds: string[];
}

/**
 * DSD 표 안에서 하나의 축 member 로 배정된 셀 영역.
 *
 * 축이 다르면 겹치는 것이 정상이라, 셀 속성으로 싣는 것은 **한 번에 한 축**이다
 * (``reviewAxisId``). 면색·이름표 채널이 하나뿐이라 축을 겹쳐 칠하면 어느 축의 색인지
 * 알 수 없다.
 */
export interface DsdMemberRegionView {
  dlViewId: string;
  axisId: string;
  /** 선택 단위 키 — ``dlViewId::axisId``. 축 id 만으로는 DL 간에 갈리지 않는다. */
  axisKey: string;
  /** 그 DL 의 제목 — 축 선택 목록에서 어느 표의 축인지 가르는 데 쓴다. */
  dlTitle?: string | null;
  memberId: string;
  /** 그 member 의 한글 이름표. 상위가 **그 DL 의 표** 에서 찾아 붙인다(못 찾으면 없음). */
  title?: string | null;
  dsdIds: string[];
}

interface DsdPreviewRendererProps {
  chunkId: string;
  subChunks: DSDSubChunkInfo[];
  focusedDsdResetKey?: number;
  onValueClick?: (
    value: string,
    matchedIxdCells: MatchedIxdCell[],
    mostProperCellId?: string | null,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  onShouldTagCellClick?: (
    value: string,
    inferredCellId: string | null,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  /** 매칭 결과가 없는(default) 셀이 클릭됐을 때 — IXD focus 정리용 */
  onNoMatchCellClick?: () => void;
  /**
   * IXD 선택에 매칭되는 DSD 셀 dsdId 집합.
   * - null/undefined: 강조 없음 — 기존 표시 유지
   * - Set: 매칭 셀에 outline, 매칭 안 된 셀은 opacity-40 으로 dim
   */
  counterpartDsdIds?: Set<string> | null;
  /** 헤더 셀 dsdId → 매핑된 dimension 태그. 우클릭 태그 popover 에 사용. */
  headerTagsByDsdId?: Map<string, DsdHeaderTag> | null;
  /** element/member id → 한글 라벨. inferredCellId 파싱 결과를 라벨링한다. */
  ixdLabelMap?: Map<string, string> | null;
  /**
   * 우클릭한 데이터 셀의 `inferredCellId` 로 IXD 셀을 포커싱하도록 부모에 위임한다.
   */
  onInferredFocus?: (
    inferredCellId: string,
    value: string,
    dlViewId?: string | null,
    period?: string | null,
  ) => void;
  /** 우클릭한 헤더 셀 → 대응 IXD 헤더로 포커싱하도록 위임한다. */
  onHeaderFocus?: (tag: DsdHeaderTag) => void;
  /** 드래그로 선택된 DSD 셀 dsdId 목록 변경 콜백(매칭 IXD 셀 강조용). */
  onSelectedCellIdsChange?: (dsdIds: string[]) => void;
  /**
   * 이 chunk 의 DL 영역들. 셀에 `data-dl-view-id` 로 실리고, 클릭 시 어느 DL 로
   * 포커싱할지도 여기서 나온다.
   */
  dlRegions?: DsdDlRegionView[] | null;
  /** 이 chunk 의 기간 영역들. 셀에 `data-period` 로 실린다. */
  periodRegions?: DsdPeriodRegionView[] | null;
  /** 이 chunk 의 line_item 영역들. 셀에 `data-line-item-id` 로 실린다. */
  lineItemRegions?: DsdLineItemRegionView[] | null;
  /** 이 chunk 의 (DL, 축) 별 member 영역들. ``reviewAxisId`` 조합만 셀에 실린다. */
  memberRegions?: DsdMemberRegionView[] | null;
  /**
   * 셀에 실을 **(DL, 축)** 키(``axisKey``). 없으면 member 속성을 안 싣는다.
   *
   * 렌더러가 검토 모드를 아는 유일한 지점이다 — DL·기간·항목과 달리 member 는 한 셀이
   * 축마다 하나씩 갖는데 면색·이름표 채널은 하나뿐이라, 어느 축을 실을지는 밖에서
   * 정해줘야 한다. 모드 자체는 여전히 CSS 가 본다.
   */
  reviewAxisId?: string | null;
  /** 값 위치 검토가 켜져 있을 때만 — 머리 칸 표시·줄 강조를 그린다. 상태는 상위가 갖는다 */
  positionReview?: DsdPositionReview | null;
}

/** 값 위치 검토 중 DSD 표에 그릴 것. 렌더러는 검토 상태를 갖지 않고 그리기만 한다 */
export interface DsdPositionReview {
  summary: PositionCheckSummary;
  currentKey: string | null;
  /** 지금 줄의 칸 — 테두리로 강조한다 */
  highlighted: ReadonlySet<string> | null;
  onJump: (lineKey: string) => void;
  /** 셀을 직접 고르면 검토 강조를 걷는다 */
  onClearHighlight: () => void;
  /** 지금 줄의 머리 칸 옆에 붙일 검토 카드 */
  currentCard?: ReactNode;
}

// ============================================
// matchStatus → DsdMatchResult 변환
// ============================================

const toMatchResult = (status?: DsdCellMatchStatus | null): DsdMatchResult => {
  switch (status) {
    case "matched":
      return "match";
    case "negated":
      return "sign-reversed";
    case "rounded":
      return "rounded";
    case "should-tag":
      return "should-tag";
    default:
      return "default";
  }
};

// ============================================
// "IXD에 값이 없습니다" 툴팁 콘텐츠 스타일
// Figma: 3912-52563 — Caption 2/Medium, Primary/Base, Shadow/Medium
// shadcn TooltipContent의 기본 스타일을 Figma 토큰으로 오버라이드
// ============================================

const NO_IXD_TOOLTIP_CLASS = cn(
  "tooltip-passthrough",
  "px-1 py-0.5 rounded-[2px] border-none box-border pointer-events-none",
  "bg-primary-base text-static-white caption-2-medium",
  "shadow-[0_2px_4px_4px_#0000000F,0_4px_6px_-1px_#0000001A]",
);

// ============================================
// DSD 테이블 블록 렌더링
// ============================================

interface DsdTableBlockProps {
  focusedDsdId: string | null;
  setFocusedDsdId: (dsdId: string | null) => void;
  cells: (DsdParsedCell | null)[][];
  counterpartDsdIds?: Set<string> | null;
  /** dsdId → 그 칸 오른쪽 위에 겹쳐 띄울 표시(IXD 위치 확인 점) */
  cornerSlotByDsdId?: Map<string, ReactNode> | null;
  /** 「이 줄 값 보기」 로 강조할 칸 */
  positionHighlightDsdIds?: ReadonlySet<string> | null;
  /** 머리 칸 dsdId → 그 칸이 머리인 확인할 줄. 머리 칸을 누르면 점을 누른 것처럼 연다 */
  positionLineKeyByDsdId?: Map<string, string> | null;
  onPositionLineToggle?: (lineKey: string) => void;
  /** dsdId → 그 셀이 배정된 DL view id. 셀에 `data-dl-view-id` 로 실린다. */
  dlViewIdByDsdId?: Map<string, string> | null;
  /** dsdId → 그 DL 의 chunk 안 순번. 셀에 `data-dl-index` 로 실린다(검토 모드 색 배정용). */
  dlIndexByDsdId?: Map<string, string> | null;
  /** dsdId → 그 DL 의 한글 제목. 셀에 `data-dl-title` 로 실린다(검토 모드 이름표용). */
  dlTitleByDsdId?: Map<string, string> | null;
  /** dsdId → 그 셀이 배정된 기간. 셀에 `data-period` 로 실린다. */
  periodByDsdId?: Map<string, string> | null;
  /** dsdId → 그 셀이 배정된 line_item 태그 id. 셀에 `data-line-item-id` 로 실린다. */
  lineItemIdByDsdId?: Map<string, string> | null;
  /** dsdId → 그 항목의 표 안 순번. 셀에 `data-line-item-index` 로 실린다(검토 모드 색). */
  lineItemIndexByDsdId?: Map<string, string> | null;
  /** dsdId → 그 line_item 의 한글 이름표. 셀에 `data-line-item-title` 로 실린다. */
  lineItemTitleByDsdId?: Map<string, string> | null;
  /** dsdId → 선택된 축에서 그 셀에 배정된 member. 셀에 `data-member-id` 로 실린다. */
  memberIdByDsdId?: Map<string, string> | null;
  /** dsdId → 그 member 의 표 안 순번(검토 모드 색). */
  memberIndexByDsdId?: Map<string, string> | null;
  /** dsdId → 그 member 의 한글 이름표. */
  memberTitleByDsdId?: Map<string, string> | null;
  /** 셀 클릭 시 호출되는 dispatcher — 키보드 navigation 과 공유한다 */
  onCellInteract: (cell: DsdParsedCell) => void;
  /** 현재 태그 popover 가 열린 셀 dsdId */
  tagPopoverDsdId?: string | null;
  /** 우클릭 셀 — 태그 popover + 대응 IXD 포커싱은 상위에서 처리한다. */
  onCellContextMenu?: (cell: DsdParsedCell) => void;
}

const DsdTableBlock = memo(function DsdTableBlock({
  focusedDsdId,
  setFocusedDsdId,
  cells,
  counterpartDsdIds,
  cornerSlotByDsdId,
  positionHighlightDsdIds,
  positionLineKeyByDsdId,
  onPositionLineToggle,
  dlViewIdByDsdId,
  dlIndexByDsdId,
  dlTitleByDsdId,
  periodByDsdId,
  lineItemIdByDsdId,
  lineItemIndexByDsdId,
  lineItemTitleByDsdId,
  memberIdByDsdId,
  memberIndexByDsdId,
  memberTitleByDsdId,
  onCellInteract,
  tagPopoverDsdId,
  onCellContextMenu,
}: DsdTableBlockProps) {
  // IXD 선택에 매칭되는 DSD 셀이 있으면(counterpartDsdIds 비-null) 매칭 강조 + 나머지 dim.
  const counterpartActive = counterpartDsdIds != null;
  const totalRows = cells.length;

  // DL 이름표를 실을 셀 — 표 안에서 그 DL 이 처음 나오는 셀 하나뿐이다. 배정된 셀마다
  // 붙이면 같은 이름이 영역 전체에 반복 인쇄돼 표를 읽을 수 없다(영역 색이 범위를,
  // 이름표는 그 색이 무엇인지를 알린다).
  const dlTitleAnchorDsdIds = useMemo(() => {
    const seenDlViewIds = new Set<string>();
    const anchors = new Set<string>();
    for (const row of cells)
      for (const cell of row) {
        if (!cell?.isOrigin) continue;
        const dlViewId = dlViewIdByDsdId?.get(cell.dsdId);
        if (!dlViewId || seenDlViewIds.has(dlViewId)) continue;
        if (!dlTitleByDsdId?.get(cell.dsdId)) continue;
        seenDlViewIds.add(dlViewId);
        anchors.add(cell.dsdId);
      }
    return anchors;
  }, [cells, dlViewIdByDsdId, dlTitleByDsdId]);

  // 항목 이름표도 같은 이유로 항목당 셀 하나에만 싣는다. DL 과 달리 한 표에 항목이
  // 수십 개라, 셀마다 붙이면 표가 이름표로 덮인다.
  const lineItemTitleAnchorDsdIds = useMemo(() => {
    const seenLineItemIds = new Set<string>();
    const anchors = new Set<string>();
    for (const row of cells)
      for (const cell of row) {
        if (!cell?.isOrigin) continue;
        const lineItemId = lineItemIdByDsdId?.get(cell.dsdId);
        if (!lineItemId || seenLineItemIds.has(lineItemId)) continue;
        if (!lineItemTitleByDsdId?.get(cell.dsdId)) continue;
        seenLineItemIds.add(lineItemId);
        anchors.add(cell.dsdId);
      }
    return anchors;
  }, [cells, lineItemIdByDsdId, lineItemTitleByDsdId]);

  // member 이름표도 같은 이유로 member 당 셀 하나에만 싣는다.
  const memberTitleAnchorDsdIds = useMemo(() => {
    const seenMemberIds = new Set<string>();
    const anchors = new Set<string>();
    for (const row of cells)
      for (const cell of row) {
        if (!cell?.isOrigin) continue;
        const memberId = memberIdByDsdId?.get(cell.dsdId);
        if (!memberId || seenMemberIds.has(memberId)) continue;
        if (!memberTitleByDsdId?.get(cell.dsdId)) continue;
        seenMemberIds.add(memberId);
        anchors.add(cell.dsdId);
      }
    return anchors;
  }, [cells, memberIdByDsdId, memberTitleByDsdId]);

  return (
    <TooltipProvider delayDuration={0}>
      {/* 표 경계 전용 — 포커싱의 "표" 단위. 가로 스크롤 노드는 이 래퍼가 아니라
          TableScrollArea 내부의 Radix Viewport 다(`scrollLeft` 를 쓰려면 그쪽).
          스크롤바는 IXD 표와 같은 TableScrollArea 가 그린다 — 네이티브 스크롤바는
          global.css 의 `*` 규칙이 앱 전역에서 숨긴다.
          이 래퍼에 overflow-x-auto 를 되돌리지 말 것 — sticky 스크롤바가 세로
          스크롤포트 대신 여기에 붙어 증상이 재발한다(중간 조상은 overflow-clip). */}
      <TableScrollArea {...{ [ATTR.tableBlock]: "" }} className="rounded-[6px]">
        <table
          className={cn(
            "border-separate border-spacing-0 rounded-[6px] w-max min-w-full",
            "[&_tr>*]:border-b [&_tr>*]:border-r [&_tr>*]:border-line-normal",
            "[&_tr>*]:border-t-0 [&_tr>*]:border-l-0",
            "[&_tr:first-child>*]:border-t [&_tr>*:first-child]:border-l",
          )}
        >
          <tbody>
            {cells.map((row, rowIdx) => {
              const isFirstRow = rowIdx === 0;
              const isLastRow = rowIdx === totalRows - 1;
              const rowTotalCols = row.reduce(
                (sum, c) =>
                  sum + (c && c.isOrigin ? (c.colspan > 1 ? c.colspan : 1) : 0),
                0,
              );

              return (
                <tr key={rowIdx}>
                  {row.map((cell, colIdx) => {
                    if (!cell || !cell.isOrigin) return null;

                    const isFirstCol = colIdx === 0;
                    const isLastCol =
                      colIdx + (cell.colspan > 1 ? cell.colspan : 1) >=
                      rowTotalCols;

                    const status = cell.matchStatus;
                    const matchResult = toMatchResult(status);
                    const sumDiffLabel =
                      status === "rounded" ? roundedDiffLabel(cell) : undefined;
                    const isClickable =
                      status === "matched" ||
                      status === "negated" ||
                      status === "rounded" ||
                      status === "should-tag";
                    // 우클릭으로 태그 popover 가 열린 셀에 ring 표시.
                    const isTagPopoverOpen = tagPopoverDsdId === cell.dsdId;

                    const cornerClass = cn(
                      isFirstRow && isFirstCol && "rounded-tl-[6px]",
                      isFirstRow && isLastCol && "rounded-tr-[6px]",
                      isLastRow && isFirstCol && "rounded-bl-[6px]",
                      isLastRow && isLastCol && "rounded-br-[6px]",
                    );

                    const cellStyle = parseCssStyle(cell.style);

                    const positionLineKey = positionLineKeyByDsdId?.get(
                      cell.dsdId,
                    );
                    const handleClick = () => {
                      setFocusedDsdId(cell.dsdId);
                      onCellInteract(cell);
                      if (positionLineKey)
                        onPositionLineToggle?.(positionLineKey);
                    };

                    // 우클릭 → 태그 popover + 대응 IXD 포커싱(상위 openTagPopover).
                    // 좌클릭 focus(=focusedDsdId)는 거둬서 좌클릭 스타일(focus ring·툴팁)이
                    // 섞이지 않게 하고, ring 은 openTagPopover 의 popover 상태로만 그린다.
                    const handleContextMenu = (event: React.MouseEvent) => {
                      event.preventDefault();
                      setFocusedDsdId(null);
                      onCellContextMenu?.(cell);
                    };

                    const isFocused = focusedDsdId === cell.dsdId;
                    const showRing = isFocused || isTagPopoverOpen;
                    // 매칭됐어야 할 숫자 값 칸에서만 알린다 — 머리·이름·「-」 칸은 IXD 와 맞출
                    // 대상이 아니라 「값이 없다」 가 틀린 말이다.
                    const tooltipText =
                      isFocused &&
                      !isClickable &&
                      DsdCellText.of(cell.text).isNumericValue
                        ? "IXD에 값이 없습니다."
                        : null;

                    const isCounterpart =
                      counterpartDsdIds?.has(cell.dsdId) ?? false;
                    const dimOverlayClass = counterpartActive
                      ? isCounterpart
                        ? REVIEW_COUNTERPART_CLASS
                        : REVIEW_DIM_CLASS
                      : positionHighlightDsdIds?.has(cell.dsdId)
                        ? REVIEW_COUNTERPART_CLASS
                        : null;

                    // 배정 정보는 DOM 에 싣는다 — 스타일·정적 다운로드본·디버깅이
                    // 같은 소스를 본다. **속성 부재가 "미배정"** 이라 빈 값은 안 붙인다.
                    const dlViewId = dlViewIdByDsdId?.get(cell.dsdId);
                    const dlIndex = dlIndexByDsdId?.get(cell.dsdId);
                    const dlTitle = dlTitleAnchorDsdIds.has(cell.dsdId)
                      ? dlTitleByDsdId?.get(cell.dsdId)
                      : undefined;
                    const period = periodByDsdId?.get(cell.dsdId);
                    const lineItemId = lineItemIdByDsdId?.get(cell.dsdId);
                    const lineItemIndex = lineItemIndexByDsdId?.get(cell.dsdId);
                    const lineItemTitle = lineItemTitleAnchorDsdIds.has(
                      cell.dsdId,
                    )
                      ? lineItemTitleByDsdId?.get(cell.dsdId)
                      : undefined;
                    const memberId = memberIdByDsdId?.get(cell.dsdId);
                    const memberIndex = memberIndexByDsdId?.get(cell.dsdId);
                    const memberTitle = memberTitleAnchorDsdIds.has(cell.dsdId)
                      ? memberTitleByDsdId?.get(cell.dsdId)
                      : undefined;
                    const regionAttrs = {
                      ...(dlViewId && { [ATTR.dlViewId]: dlViewId }),
                      ...(dlIndex && { [ATTR.dlIndex]: dlIndex }),
                      ...(dlTitle && { [ATTR.dlTitle]: dlTitle }),
                      ...(period && { [ATTR.period]: period }),
                      ...(lineItemId && { [ATTR.lineItemId]: lineItemId }),
                      ...(lineItemIndex && {
                        [ATTR.lineItemIndex]: lineItemIndex,
                      }),
                      ...(lineItemTitle && {
                        [ATTR.lineItemTitle]: lineItemTitle,
                      }),
                      ...(memberId && { [ATTR.memberId]: memberId }),
                      ...(memberIndex && { [ATTR.memberIndex]: memberIndex }),
                      ...(memberTitle && { [ATTR.memberTitle]: memberTitle }),
                    };

                    const cornerSlot = cornerSlotByDsdId?.get(cell.dsdId);
                    const cellText = DsdCellText.of(cell.text);
                    const cellEl =
                      cell.tagName === "th" ? (
                        <DsdTable.Header
                          type="column"
                          matchResult={matchResult}
                          cornerSlot={cornerSlot}
                          {...{ [ATTR.cellId]: cell.dsdId }}
                          {...regionAttrs}
                          rowSpan={cell.rowspan > 1 ? cell.rowspan : undefined}
                          colSpan={cell.colspan > 1 ? cell.colspan : undefined}
                          style={cellStyle}
                          className={cn(
                            isClickable && "cursor-pointer",
                            showRing && REVIEW_FOCUS_CLASS,
                            dimOverlayClass,
                            cornerClass,
                          )}
                          {...(positionLineKey && {
                            [POSITION_REVIEW_CONTROL_ATTR]: "",
                          })}
                          onClick={handleClick}
                          onContextMenu={handleContextMenu}
                        >
                          {cellText.display}
                        </DsdTable.Header>
                      ) : (
                        <DsdTable.Cell
                          matchResult={matchResult}
                          sumDiffLabel={sumDiffLabel}
                          cornerSlot={cornerSlot}
                          {...selectableCellAttrs({
                            uniqueId: dsdCellDomId(cell.dsdId),
                            cellId: cell.dsdId,
                            displayValue: cellText.clipboard,
                          })}
                          {...regionAttrs}
                          rowSpan={cell.rowspan > 1 ? cell.rowspan : undefined}
                          colSpan={cell.colspan > 1 ? cell.colspan : undefined}
                          style={cellStyle}
                          className={cn(
                            isClickable && "cursor-pointer",
                            showRing && REVIEW_FOCUS_CLASS,
                            dimOverlayClass,
                            cornerClass,
                          )}
                          {...(positionLineKey && {
                            [POSITION_REVIEW_CONTROL_ATTR]: "",
                          })}
                          onClick={handleClick}
                          onContextMenu={handleContextMenu}
                        >
                          {cellText.display}
                        </DsdTable.Cell>
                      );

                    return (
                      <Tooltip key={colIdx} open={tooltipText != null}>
                        <TooltipTrigger asChild>{cellEl}</TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="center"
                          sideOffset={2}
                          className={NO_IXD_TOOLTIP_CLASS}
                        >
                          {tooltipText}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScrollArea>
    </TooltipProvider>
  );
});

// ============================================
// 문단 블록 렌더링
// ============================================

function findMatchesForParagraph(
  text: string,
  matches: DsdSentenceMatch[] | null | undefined,
): DsdSentenceMatch[] {
  if (!matches?.length) return [];
  return matches.filter((m) => m.value && text.includes(m.value));
}

const INLINE_HIGHLIGHT_CLASS: Record<string, string> = {
  matched: "bg-green-200 hover:bg-green-300 rounded px-0.5 cursor-pointer",
  negated: "bg-amber-200 hover:bg-amber-300 rounded px-0.5 cursor-pointer",
  rounded: "bg-yellow-200 hover:bg-yellow-300 rounded px-0.5 cursor-pointer",
  "should-tag": "bg-red-100 hover:bg-red-200 rounded px-0.5 cursor-pointer",
};

/** IXD 에서 못 찾은 문장주석 금액. 표 셀의 "확인 필요" 빨강과 같은 톤이고, 대응 IXD 셀이 없어 클릭 대상이 아니다. */
const MISSING_WON_CLASS = "bg-rose-200 rounded px-0.5";

/** '원' 으로 끝나는 금액 — 미매칭을 빨강으로 드러내는 대상. 다른 값(날짜·%·주/건)은 오늘 그대로 둔다. */
const isWonAmount = (value: string): boolean => /원$/.test(value);

const inlineHighlightClass = (match: DsdSentenceMatch): string | undefined =>
  match.matchStatus === "default"
    ? isWonAmount(match.value)
      ? MISSING_WON_CLASS
      : undefined
    : INLINE_HIGHLIGHT_CLASS[match.matchStatus];

/** 포커싱할 IXD 셀을 가리키는 상태 — 클릭 분기·data-* 노출의 단일 기준. */
const HAS_IXD_TARGET: ReadonlySet<DsdCellMatchStatus> = new Set([
  "matched",
  "negated",
  "rounded",
]);

/** 포커싱할 IXD 셀이 실제로 실려 있는 매칭. 상태만 맞고 셀이 비면 클릭해도 할 일이 없다. */
const hasIxdTarget = (match: DsdSentenceMatch): boolean =>
  HAS_IXD_TARGET.has(match.matchStatus) && !!match.matchedIxdCells?.length;

/** should-tag 는 IXD 셀 대신 ``inferredCellId`` 로 추천 위치를 연다. */
const isClickableMatch = (match: DsdSentenceMatch): boolean =>
  hasIxdTarget(match) || match.matchStatus === "should-tag";

/**
 * 값이 텍스트에 등장하는 시작 위치들.
 *
 * ``guard`` 는 더 큰 금액의 꼬리를 건너뛴다 — "45,000원" 안의 "5,000원" 처럼. 오늘은
 * 미매칭이 무색이라 보이지 않지만 빨강이 붙으면 눈에 보이는 오탐이 된다.
 *
 * 콤마는 **숫자 뒤에 있을 때만** 자릿수 구분자다. "2,000원,1,000원" 처럼 나열 구분자로
 * 쓰인 콤마까지 꼬리로 보면 멀쩡한 금액이 색을 잃는다.
 */
function valueOccurrences(
  text: string,
  value: string,
  guard: boolean,
): number[] {
  const found: number[] = [];
  let searchFrom = 0;
  while (searchFrom < text.length) {
    const idx = text.indexOf(value, searchFrom);
    if (idx === -1) break;
    const prev = text[idx - 1] ?? "";
    const beforePrev = text[idx - 2] ?? "";
    const isTail = /\d/.test(prev) || (prev === "," && /\d/.test(beforePrev));
    if (!guard || !isTail) found.push(idx);
    searchFrom = idx + value.length;
  }
  return found;
}

function renderHighlightedText(
  text: string,
  matches: DsdSentenceMatch[],
  onValueClick?: DsdPreviewRendererProps["onValueClick"],
  onShouldTagCellClick?: DsdPreviewRendererProps["onShouldTagCellClick"],
  // 셀 하나짜리 표는 이 경로로 렌더되면서도 dsdId 가 있어 DL·기간을 안다. 표 셀 경로와
  // 같은 인자를 넘겨야 "같은 클릭이 렌더 경로에 따라 다르게 동작" 하지 않는다.
  // 진짜 문단(문장 매칭)에는 배정이 없어 둘 다 null 이다.
  region?: { dlViewId: string | null; period: string | null },
): ReactNode {
  if (!matches.length) return text;

  // 동일 값이 여러 번 등장할 수 있으므로 반복 탐색
  const segments: { start: number; end: number; match: DsdSentenceMatch }[] =
    [];
  const matchesByValue = new Map<string, DsdSentenceMatch[]>();
  for (const m of matches) {
    if (!m.value) continue;
    const sameValue = matchesByValue.get(m.value);
    if (sameValue) sameValue.push(m);
    else matchesByValue.set(m.value, [m]);
  }
  for (const [value, valueMatches] of matchesByValue) {
    const won = isWonAmount(value);
    valueOccurrences(text, value, won).forEach((start, i) => {
      // '원' 금액만 k번째 등장 ↔ k번째 매칭으로 짝짓는다. 같은 금액이 matched 와
      // default 로 두 번 잡히면 첫 매칭 색을 공유하는 오늘 규칙이 빨강을 지워버린다.
      // 문단 경로는 ``pickMatchesPerParagraph`` 가 등장 수만큼 자리를 채워 넘기므로
      // 여기 fallback 은 매칭이 하나뿐인 단일셀 표 경로에서만 쓰인다.
      const match = won
        ? (valueMatches[i] ?? valueMatches[0])
        : valueMatches[0];
      segments.push({ start, end: start + value.length, match });
    });
  }
  if (!segments.length) return text;

  segments.sort((a, b) => a.start - b.start);

  const parts: ReactNode[] = [];
  let cursor = 0;
  let keyIdx = 0;

  for (const seg of segments) {
    if (seg.start < cursor) continue; // 겹치는 segment 건너뛰기

    if (cursor < seg.start) {
      parts.push(text.slice(cursor, seg.start));
    }

    const cls = inlineHighlightClass(seg.match);
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const m = seg.match;
      if (hasIxdTarget(m)) {
        // 좁히는 기준은 **정본 셀의** DL·기간이다. DSD 값의 배정은 그 IXD 셀이 어디
        // 있는지에 대한 추정일 뿐이고, 고른 쪽만 실제 위치를 안다.
        const proper = m.mostProperCell;
        onValueClick?.(
          m.value,
          m.matchedIxdCells ?? [],
          proper?.cellId,
          proper?.dlViewId ?? null,
          // 기간만 이 값의 배정으로 물러선다. 문장영역 태그는 기간 개념이 없어 정본
          // cell_id 의 기간이 비는데(`:X::`), IXD 표는 그 fact 를 어느 기간 열엔가
          // 그리므로 접미사 일치로 두 기간이 함께 잡힌다. 안 좁히면 전기 셀로 간다.
          (proper && periodOfCellId(proper.cellId)) ?? region?.period ?? null,
        );
      } else if (m.matchStatus === "should-tag") {
        onShouldTagCellClick?.(
          m.value,
          m.inferredCellId ?? null,
          region?.dlViewId ?? null,
          region?.period ?? null,
        );
      }
    };

    // 정적 HTML 다운로드본에서 인라인 매칭 값을 클릭하면 IXD 셀로 포커스할 수 있게
    // 매칭 IXD cellId 를 data-* 로 노출한다(라이브 동작에는 영향 없음).
    const segMatch = seg.match;
    const segIxdIds = hasIxdTarget(segMatch)
      ? (segMatch.matchedIxdCells ?? []).map((c) => c.cellId).filter(Boolean)
      : [];
    const segProperCellId = segMatch.mostProperCell?.cellId;
    const segPrimary =
      segProperCellId && segIxdIds.indexOf(segProperCellId) >= 0
        ? segProperCellId
        : segIxdIds[0];

    parts.push(
      <span
        key={keyIdx++}
        className={cls}
        onClick={isClickableMatch(seg.match) ? handleClick : undefined}
        {...(segIxdIds.length
          ? {
              [ATTR.pairCellId]: segIxdIds.join("\n"),
              [ATTR.primaryCellId]: segPrimary,
            }
          : {})}
      >
        {seg.match.value}
      </span>,
    );

    cursor = seg.end;
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
}

interface DsdParagraphCellProps {
  paragraph: DsdParagraphItem;
  matches: DsdSentenceMatch[];
  className?: string;
  onValueClick?: DsdPreviewRendererProps["onValueClick"];
  onShouldTagCellClick?: DsdPreviewRendererProps["onShouldTagCellClick"];
  /** 셀 하나짜리 표로 렌더될 때의 DL·기간 배정. 진짜 문단이면 없다. */
  region?: { dlViewId: string | null; period: string | null };
}

const DsdParagraphCell = ({
  paragraph,
  matches,
  onValueClick,
  className,
  onShouldTagCellClick,
  region,
}: DsdParagraphCellProps) => (
  <DsdTable.NarrativeBlock
    matchResult="default"
    className={cn("max-w-none w-full", className)}
  >
    <p>
      {renderHighlightedText(
        paragraph.text,
        matches,
        onValueClick,
        onShouldTagCellClick,
        region,
      )}
    </p>
  </DsdTable.NarrativeBlock>
);

interface DsdParagraphBlockProps {
  paragraphs: DsdParagraphItem[];
  sentenceMatches?: DsdSentenceMatch[] | null;
  onValueClick?: DsdPreviewRendererProps["onValueClick"];
  onShouldTagCellClick?: DsdPreviewRendererProps["onShouldTagCellClick"];
}

/**
 * 문단별로 매칭을 고르되 '원' 금액은 **블록 전체에서 등장 순서대로 소비**한다.
 *
 * ``findMatchesForParagraph`` 는 값 문자열 포함만 보므로, 한 블록이 여러 문단으로
 * 쪼개지면 같은 금액의 매칭을 문단마다 전부 받는다 — 문단별로 첫 매칭을 쓰면 뒤 문단의
 * default(빨강)가 앞 문단의 matched 에 덮인다. 등장 수만큼만 잘라 쓰고 남은 매칭은
 * 버린다: 남는 건 같은 sub_chunk 의 다른 표·문단 매칭이 흘러든 것이라 이 블록 등장에
 * 대한 판정이 아니고, 그걸로 덮으면 멀쩡한 초록이 빨개진다.
 */
function pickMatchesPerParagraph(
  paragraphs: DsdParagraphItem[],
  sentenceMatches: DsdSentenceMatch[] | null | undefined,
): DsdSentenceMatch[][] {
  const consumed = new Map<string, number>();
  return paragraphs.map((p) => {
    const candidates = findMatchesForParagraph(p.text, sentenceMatches);
    const picked: DsdSentenceMatch[] = [];
    const done = new Set<string>();
    for (const m of candidates) {
      if (!isWonAmount(m.value)) {
        picked.push(m);
        continue;
      }
      if (done.has(m.value)) continue; // 같은 값은 아래에서 한 번에 처리
      done.add(m.value);
      const sameValue = candidates.filter((c) => c.value === m.value);
      const used = consumed.get(m.value) ?? 0;
      const count = valueOccurrences(p.text, m.value, true).length;
      // 등장 수만큼 자리를 채운다 — 남은 매칭이 모자란 등장은 블록의 첫 매칭(오늘 색)으로
      // 물러선다. 잔여 매칭으로 물러서면 미매칭 하나가 빨강 여러 개로 부풀어난다.
      picked.push(
        ...Array.from(
          { length: count },
          (_, i) => sameValue[used + i] ?? sameValue[0],
        ),
      );
      consumed.set(m.value, Math.min(used + count, sameValue.length));
    }
    return picked;
  });
}

const DsdParagraphBlock = ({
  paragraphs,
  sentenceMatches,
  onValueClick,
  onShouldTagCellClick,
}: DsdParagraphBlockProps) => {
  const matchesPerParagraph = pickMatchesPerParagraph(
    paragraphs,
    sentenceMatches,
  );
  return (
    <>
      {paragraphs.map((p, i) => (
        <DsdParagraphCell
          key={i}
          paragraph={p}
          matches={matchesPerParagraph[i]}
          className="w-full"
          onValueClick={onValueClick}
          onShouldTagCellClick={onShouldTagCellClick}
        />
      ))}
    </>
  );
};

// ============================================
// HTML 블록 (레거시 서브청크 — 비-테이블 Raw HTML)
// ============================================

const findCellByDsdId = (
  container: HTMLElement,
  dsdId: string,
): HTMLElement | null =>
  container.querySelector<HTMLElement>(
    `[${ATTR.cellId}="${CSS.escape(dsdId)}"]`,
  );

// IXD 선택 기반 DSD 셀 스크롤 — onCellInteract 를 호출하지 않아 클릭/키보드 흐름과 분리된다.
// rAF 는 IXD 선택으로 바뀐 DOM 이 반영된 뒤 위치를 재는 용도라 그대로 둔다.
function scrollDsdCellIntoView(
  container: HTMLElement | null,
  dsdId: string,
  prevDsdId: string | null,
) {
  requestAnimationFrame(() => {
    if (!container) return;
    const cell = findCellByDsdId(container, dsdId);
    if (!cell) return;
    applyFocusScroll(
      planFocusScroll({
        cell,
        prevCell: prevDsdId ? findCellByDsdId(container, prevDsdId) : null,
        container,
        sectionSelector: DSD_SECTION_SELECTOR,
      }),
    );
  });
}

// ============================================
// 메인 컴포넌트
// ============================================

export const DsdPreviewRenderer = forwardRef<
  DsdPreviewRendererHandle,
  DsdPreviewRendererProps
>(function DsdPreviewRenderer(
  {
    chunkId,
    subChunks,
    focusedDsdResetKey,
    onValueClick,
    onShouldTagCellClick,
    onNoMatchCellClick,
    counterpartDsdIds,
    headerTagsByDsdId,
    ixdLabelMap,
    onInferredFocus,
    onHeaderFocus,
    onSelectedCellIdsChange,
    dlRegions,
    periodRegions,
    lineItemRegions,
    memberRegions,
    reviewAxisId,
    positionReview,
  }: DsdPreviewRendererProps,
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const positionSummary = positionReview?.summary ?? null;
  const positionCurrentKey = positionReview?.currentKey ?? null;
  const jumpToPositionLine = positionReview?.onJump;
  const clearPositionHighlight = positionReview?.onClearHighlight;
  const positionCurrentCard = positionReview?.currentCard;

  // 표마다 머리 칸 → 그 칸 모서리에 띄울 표시. 이름이 같은 줄은 표시하지 않는다.
  // 도구를 켜기 전에는 표에 아무것도 띄우지 않는다 — 켜겠다고 정한 사람에게만 보인다.
  const positionSlotsByBlock = useMemo(() => {
    if (!positionSummary || !jumpToPositionLine) return null;
    const flagByKey = new Map(
      positionSummary.flagged.map((line) => [line.key, line.flag]),
    );
    const slots = new Map<string, Map<string, ReactNode>>();
    const lineKeys = new Map<string, Map<string, string>>();
    for (const [
      blockKey,
      counterparts,
    ] of positionSummary.counterpartsByBlock) {
      const byAnchor = new Map<string, ReactNode>();
      const keyByAnchor = new Map<string, string>();
      for (const [anchorDsdId, group] of groupByAnchor(counterparts)) {
        const markers = group.flatMap((counterpart) => {
          const key = lineKey(blockKey, counterpart);
          const flag = flagByKey.get(key);
          if (!flag) return [];
          if (!keyByAnchor.has(anchorDsdId)) keyByAnchor.set(anchorDsdId, key);
          return [
            <PositionCheckMarker
              key={key}
              flag={flag}
              lineKey={key}
              axis={counterpart.axis}
              onJump={() => jumpToPositionLine(key)}
            />,
          ];
        });
        if (markers.length) byAnchor.set(anchorDsdId, markers);
      }
      slots.set(blockKey, byAnchor);
      lineKeys.set(blockKey, keyByAnchor);
    }
    return { slots, lineKeys };
  }, [positionSummary, jumpToPositionLine]);

  // 지금 줄과 그 카드는 표시 맵에 넣지 않고 context 로 내린다 — 맵에 넣으면 줄을 옮길
  // 때마다 맵이 새로 만들어져 `memo` 가 깨지고 화면의 모든 표 블록이 다시 그려진다.
  const positionCurrentLine = useMemo(
    () => ({ currentKey: positionCurrentKey, card: positionCurrentCard }),
    [positionCurrentKey, positionCurrentCard],
  );
  // 같은 이유로 강조 Set 은 지금 줄이 있는 표에만 넘긴다 — 줄의 칸은 한 표 안에 있고,
  // 줄 키의 머리가 블록 키다.
  const positionHighlightBlockKey = positionCurrentKey?.split(":")[0] ?? null;

  // 우클릭 태그 popover — 셀 dsdId + 셀 종류 + 표시할 태그들.
  const [popover, setPopover] = useState<{
    dsdId: string;
    kind: ReviewCellKind;
    tags: TagDimension[];
  } | null>(null);

  // chunk 전환 / 외부 reset 시 popover 닫기.
  useEffect(() => {
    setPopover(null);
  }, [focusedDsdResetKey, chunkId]);

  // dsdId → 그 셀이 배정된 DL view id. 셀 attribute 와 클릭 포커싱이 같이 쓴다 —
  // 같은 cell_id 가 여러 DL 에 실릴 수 있어 cell_id 만으로는 DL 이 정해지지 않는다.
  const dlViewIdByDsdId = useMemo(
    () => indexByDsdId(dlRegions, (region) => region.dlViewId),
    [dlRegions],
  );
  // 제목을 못 찾은 DL 은 키를 안 만든다 — 속성 부재가 "이름 모름" 이다.
  const dlTitleByDsdId = useMemo(
    () => indexByDsdId(dlRegions, (region) => region.title ?? undefined),
    [dlRegions],
  );
  // 검토 모드가 DL 마다 다른 색을 쓰려면 CSS 로 고를 수 있는 값이 필요하다 —
  // dlViewId 는 임의 문자열이라 selector 로 못 잡는다. 색 수만큼 순환시킨다.
  //
  // 순번은 **DL 단위**로 센다. 서버는 표마다, 또 배정이 끊긴 구간마다 영역을 따로
  // 내려주므로 영역 순번으로 세면 같은 DL 의 조각들이 서로 다른 색이 된다.
  const dlIndexByDsdId = useMemo(() => {
    const colorByDlViewId = new Map<string, string>();
    return indexByDsdId(dlRegions, (region) => {
      const assigned = colorByDlViewId.get(region.dlViewId);
      if (assigned !== undefined) return assigned;
      const next = String(colorByDlViewId.size % DL_REVIEW_COLOR_COUNT);
      colorByDlViewId.set(region.dlViewId, next);
      return next;
    });
  }, [dlRegions]);
  // dsdId → 그 셀이 배정된 기간. 표시는 attribute 로만 하고 콜백에는 안 싣는다.
  const periodByDsdId = useMemo(
    () => indexByDsdId(periodRegions, (region) => region.period),
    [periodRegions],
  );
  // dsdId → 그 셀이 배정된 line_item 태그 id. 기간과 같이 표시 전용이다.
  const lineItemIdByDsdId = useMemo(
    () => indexByDsdId(lineItemRegions, (region) => region.lineItemId),
    [lineItemRegions],
  );
  // 항목별 색도 DL 과 같은 방식 — CSS 가 고를 수 있는 순번을 싣는다. 순번은 **항목
  // 단위**라 같은 항목이 표 안 여러 곳에 나와도 색이 하나다.
  const lineItemIndexByDsdId = useMemo(() => {
    const colorByLineItemId = new Map<string, string>();
    return indexByDsdId(lineItemRegions, (region) => {
      const assigned = colorByLineItemId.get(region.lineItemId);
      if (assigned !== undefined) return assigned;
      const next = String(
        colorByLineItemId.size % LINE_ITEM_REVIEW_COLOR_COUNT,
      );
      colorByLineItemId.set(region.lineItemId, next);
      return next;
    });
  }, [lineItemRegions]);
  // 선택된 (DL, 축) 의 영역만 남긴다 — 섞으면 한 셀이 여러 member 를 갖게 돼 맵이 덮이고,
  // 같은 축 tag 를 쓰는 다른 DL 의 배정이 한 덩어리로 칠해진다.
  const axisRegions = useMemo(
    () =>
      reviewAxisId
        ? (memberRegions ?? []).filter((r) => r.axisKey === reviewAxisId)
        : [],
    [memberRegions, reviewAxisId],
  );
  const memberIdByDsdId = useMemo(
    () => indexByDsdId(axisRegions, (region) => region.memberId),
    [axisRegions],
  );
  const memberIndexByDsdId = useMemo(() => {
    const colorByMemberId = new Map<string, string>();
    return indexByDsdId(axisRegions, (region) => {
      const assigned = colorByMemberId.get(region.memberId);
      if (assigned !== undefined) return assigned;
      const next = String(colorByMemberId.size % LINE_ITEM_REVIEW_COLOR_COUNT);
      colorByMemberId.set(region.memberId, next);
      return next;
    });
  }, [axisRegions]);
  const memberTitleByDsdId = useMemo(
    () => indexByDsdId(axisRegions, (region) => region.title ?? undefined),
    [axisRegions],
  );
  // 이름표를 못 찾은 항목은 키를 안 만든다 — 속성 부재가 "이름 모름" 이고, 배정 자체는
  // lineItemId 가 알린다. 문구는 표마다 다르므로 상위가 그 DL 의 표에서 찾아 넣는다
  // (`buildDsdLineItemRegionViews`).
  const lineItemTitleByDsdId = useMemo(
    () => indexByDsdId(lineItemRegions, (region) => region.title ?? undefined),
    [lineItemRegions],
  );

  // 우클릭 셀 → 태그 popover. 헤더 셀은 인식된 태그를, 데이터 셀은 백엔드가 `inferredCellId`
  // 에 담아준 위치를 파싱해 보여주고 그 셀로 IXD 를 포커싱한다(라벨은 ixdLabelMap, 없으면 raw id).
  const openTagPopover = useCallback(
    (cell: DsdParsedCell) => {
      const ownTag = headerTagsByDsdId?.get(cell.dsdId);
      if (ownTag) {
        // 한 헤더가 기간·라인아이템·멤버를 동시에 가질 수 있다. dimension 마다
        // 라벨을 따로 풀어 담는다(ownTag.label 은 1개뿐이라 멤버/라인아이템이
        // 함께 있으면 한쪽 라벨로 덮어써지므로 ixdLabelMap 으로 개별 해석).
        const tags: TagDimension[] = [];
        if (ownTag.period) tags.push({ period: ownTag.period });
        if (ownTag.lineItem)
          tags.push({
            lineItem: ownTag.lineItem,
            label: ixdLabelMap?.get(ownTag.lineItem) ?? null,
          });
        if (ownTag.member)
          tags.push({
            member: ownTag.member,
            label: ixdLabelMap?.get(ownTag.member) ?? null,
          });
        setPopover({ dsdId: cell.dsdId, kind: "header", tags });
        // 대응되는 IXD 헤더로 포커싱한다.
        onHeaderFocus?.(ownTag);
        return;
      }
      const inferred = cell.inferredCellId;
      const tags: TagDimension[] = [];
      if (inferred) {
        const parsed = parseCellId(normalizeCellId(inferred));
        if (parsed.periodType)
          tags.push({ period: parsed.periodType as PeriodType });
        if (parsed.lineItemId)
          tags.push({
            lineItem: parsed.lineItemId,
            label: ixdLabelMap?.get(parsed.lineItemId) ?? null,
          });
        for (const axis of parsed.axisIds)
          tags.push({ member: axis, label: ixdLabelMap?.get(axis) ?? null });
      }

      // 우클릭한 셀에는 항상 popover 상태를 세팅해 ring 을 그린다(태그가 없으면 카드는
      // 렌더되지 않고 ring 만 남는다). ring 을 focusedDsdId(=좌클릭 상태)로 그리지 않아
      // 좌클릭 스타일(focus ring·"IXD에 값이 없습니다" 툴팁)과 섞이지 않는다.
      setPopover({ dsdId: cell.dsdId, kind: "data", tags });
      if (inferred) {
        const parsed = parseCellId(normalizeCellId(inferred));
        const label =
          (parsed.lineItemId && ixdLabelMap?.get(parsed.lineItemId)) ||
          parsed.lineItemId ||
          cell.text;
        onInferredFocus?.(
          inferred,
          label,
          dlViewIdByDsdId.get(cell.dsdId) ?? null,
          periodByDsdId.get(cell.dsdId) ?? null,
        );
      } else {
        // inferred 가 없으면 IXD 포커싱 대상이 없으므로 기존 IXD focus 를 정리한다.
        onNoMatchCellClick?.();
      }
    },
    [
      headerTagsByDsdId,
      ixdLabelMap,
      onInferredFocus,
      onHeaderFocus,
      onNoMatchCellClick,
      dlViewIdByDsdId,
      periodByDsdId,
    ],
  );

  // 좌클릭 + 키보드 navigation 공통 dispatcher.
  const dispatchCellInteract = useCallback(
    (cell: DsdParsedCell) => {
      // focus(좌클릭/키보드)가 켜지면 우클릭 태그 popover 는 닫는다.
      // (우클릭 핸들러가 focus 를 지우는 것과 대칭 — 한 번에 한 종류의 강조만 남긴다.)
      setPopover(null);
      const status = cell.matchStatus;
      if (!status || status === "default") {
        onNoMatchCellClick?.();
        return;
      }
      const dlViewId = dlViewIdByDsdId.get(cell.dsdId) ?? null;
      const period = periodByDsdId.get(cell.dsdId) ?? null;
      if (status === "should-tag") {
        onShouldTagCellClick?.(
          cell.text,
          cell.inferredCellId ?? null,
          dlViewId,
          period,
        );
      } else if (
        status === "matched" ||
        status === "negated" ||
        status === "rounded"
      ) {
        const proper = cell.mostProperCell;
        onValueClick?.(
          cell.text,
          cell.matchedIxdCells ?? [],
          proper?.cellId,
          // 좁히는 기준은 정본 셀의 실제 위치다. 표 셀 정본은 DL·기간을 항상 갖는다.
          proper?.dlViewId ?? null,
          proper ? periodOfCellId(proper.cellId) : null,
        );
      }
    },
    [
      onValueClick,
      onShouldTagCellClick,
      onNoMatchCellClick,
      dlViewIdByDsdId,
      periodByDsdId,
    ],
  );

  // 방향키 navigation — focus 상태 + 핸들러 등록은 hook 이 담당한다.
  const { focusedDsdId, setFocusedDsdId } = useDsdCellNavigation({
    containerRef,
    subChunks,
    focusedDsdResetKey,
    onCellInteract: dispatchCellInteract,
  });

  // 스크롤 기준선용 "직전에 포커싱했던 셀". `focusedDsdId`(강조 소유자)와 수명이 다르다 —
  // IXD 의 `scrollAnchorCellRef` 와 같은 역할이다. 강조는 `clearFocus()`(매칭 DSD 가 없는
  // IXD 셀 선택·다중 선택)로 풀리는데 그때 "직전에 보던 표"까지 잊으면, 같은 표의 셀로
  // 다시 오는 다음 포커싱이 `planFocusScroll` 에서 "표가 바뀌었다" 로 판정돼 표 머리로
  // 되돌아간다. 그래서 null 은 반영하지 않는다.
  //
  // imperative handle 은 setFocusedDsdId 의존성만 갖는데(재생성을 막으려), 그 안에서 최신
  // 값을 읽어야 하므로 ref 로 둔다. effect 는 커밋 이후에만 돌아서 handle 안에서 상태를
  // 바꾸는 자리(`scrollToDsdId`)는 ref 도 **동기적으로** 함께 갱신한다 — 안 하면 같은
  // tick 에 두 번 호출됐을 때 직전 dsdId 를 그대로 들고 있다. effect 는 훅 내부
  // writer(방향키 네비게이션)만 커버한다.
  const scrollAnchorDsdIdRef = useRef<string | null>(focusedDsdId);
  useEffect(() => {
    if (focusedDsdId !== null) scrollAnchorDsdIdRef.current = focusedDsdId;
  }, [focusedDsdId]);

  // IXD 선택 → DSD 스크롤 트리거용 imperative handle.
  // onCellInteract 를 호출하지 않아 DSD 클릭/키보드 네비게이션과 상태가 섞이지 않는다.
  useImperativeHandle(
    ref,
    () => ({
      scrollToDsdId: (dsdId: string) => {
        // 직전 기준선은 덮기 전에 읽는다 — 놓치면 "표가 바뀌었나" 판정이
        // 항상 참이 되어 같은 표 안에서도 표 머리로 되돌아간다.
        const prevDsdId = scrollAnchorDsdIdRef.current;
        scrollAnchorDsdIdRef.current = dsdId;
        setFocusedDsdId(dsdId);
        scrollDsdCellIntoView(containerRef.current, dsdId, prevDsdId);
      },
      // 강조만 푼다. 스크롤 기준선(`scrollAnchorDsdIdRef`)은 남긴다 — 위 주석 참고.
      clearFocus: () => {
        setFocusedDsdId(null);
      },
      getScrollContainer: () => containerRef.current,
      getCellElement: (dsdId: string) =>
        containerRef.current
          ? findCellByDsdId(containerRef.current, dsdId)
          : null,
    }),
    [setFocusedDsdId],
  );

  // 표 셀 드래그 다중 선택(IXD 표와 동일한 공통 훅 — 컨테이너 위임 방식).
  // 단일 클릭(size 1)과 다중 선택(size>=2)을 상호 배타적으로 만든다:
  // - 다중 선택: 단일 navigation focus(ring)와 IXD 매칭 popover 를 끄고, 선택 dsdId 를 전파.
  // - 단일/빈 선택: 검은 테두리도, DSD→IXD 다중 강조도 끄고(빈 목록 전파), 클릭 navigation 이
  //   단일 focus 를 소유하게 둔다. (검은 테두리 억제는 minSelectionToStyle=2 로 처리.)
  useDsdCellSelection(containerRef, {
    onSelectionStateChange: useCallback(
      ({ isMulti, dsdIds }: { isMulti: boolean; dsdIds: string[] }) => {
        // 좌클릭/드래그 선택은 우클릭 태그 popover 와 상호 배타 — popover ring 을 닫는다.
        // (우클릭은 button 2 라 이 콜백을 트리거하지 않으므로 자기 popover 를 닫지 않는다.)
        setPopover(null);
        clearPositionHighlight?.();
        if (isMulti) {
          // 다중 선택이 단일 focus 를 이긴다: ring 제거 + IXD navigation/popover 닫기.
          setFocusedDsdId(null);
          onNoMatchCellClick?.();
        }
        onSelectedCellIdsChange?.(dsdIds);
      },
      [
        setFocusedDsdId,
        onNoMatchCellClick,
        onSelectedCellIdsChange,
        clearPositionHighlight,
      ],
    ),
  });

  // dsdId → blockKey("subIdx-blockIdx") 역방향 맵.
  // focusedDsdId/popover가 바뀔 때 어느 블록만 리렌더할지 계산하는 데 사용한다.
  const dsdIdToBlockKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const [subIdx, subChunk] of subChunks.entries()) {
      for (const [blockIdx, block] of (
        subChunk.parsedContent ?? []
      ).entries()) {
        if (block.blockType !== "table" || !block.cells) continue;
        for (const row of block.cells) {
          for (const cell of row) {
            if (cell?.isOrigin) map.set(cell.dsdId, `${subIdx}-${blockIdx}`);
          }
        }
      }
    }
    return map;
  }, [subChunks]);

  const focusedBlockKey = focusedDsdId
    ? (dsdIdToBlockKey.get(focusedDsdId) ?? null)
    : null;
  const popoverBlockKey = popover?.dsdId
    ? (dsdIdToBlockKey.get(popover.dsdId) ?? null)
    : null;

  const body = (
    <div
      ref={containerRef}
      {...{ [POSITION_CARD_BOUNDARY_ATTR]: "" }}
      // 이 스크롤포트에는 padding-bottom 을 넣지 말 것 — 표마다 붙는 TableScrollArea 의
      // 가로 스크롤바가 `sticky bottom-0` 으로 여기에 붙는데, 브라우저가 sticky 제약을
      // content box 기준으로 잡아 스크롤바가 그만큼 위로 떠 보인다(IXD 렌더러와 같은 이유).
      // 하단 여백은 아래 내부 래퍼의 `pb-5` 가 만든다 — 래퍼는 스크롤 컨테이너가 아니라
      // sticky 제약에 끼어들지 않는다.
      className="h-full bg-white overflow-y-auto pt-5 px-2 overflow-x-hidden"
    >
      {/* 하단 여백 담당 래퍼 — 스크롤포트에 padding-bottom 을 줄 수 없어서 여기 둔다.
          스크롤포트를 flex 로 두면 안 된다: 내용이 넘칠 때 flex-shrink 로 이 래퍼가
          줄어들며 pb 가 내용 위로 끌려온다. */}
      <div className="flex flex-col gap-5 pb-5">
        {subChunks.map((subChunk, subIdx) => (
          <div
            key={`${chunkId}-${subIdx}`}
            id={`${chunkId}-${subIdx + 1}`}
            className="flex flex-col gap-1"
          >
            {subChunk.parsedContent?.map((block, blockIdx) => {
              if (block.blockType === "table" && block.cells) {
                const originCells = block.cells
                  .flat()
                  .filter((c): c is DsdParsedCell => c != null && c.isOrigin);
                if (originCells.length === 1) {
                  const cell = originCells[0];
                  const cellMatches: DsdSentenceMatch[] =
                    cell.matchStatus && cell.matchStatus !== "default"
                      ? [
                          {
                            value: cell.text,
                            matchStatus: cell.matchStatus,
                            matchedIxdCells: cell.matchedIxdCells ?? undefined,
                            mostProperCell: cell.mostProperCell,
                            inferredCellId: cell.inferredCellId,
                          },
                        ]
                      : [];
                  return (
                    <DsdParagraphCell
                      key={`table-as-paragraph-${blockIdx}`}
                      paragraph={{ text: cell.text }}
                      matches={cellMatches}
                      onValueClick={onValueClick}
                      onShouldTagCellClick={onShouldTagCellClick}
                      region={{
                        dlViewId: dlViewIdByDsdId.get(cell.dsdId) ?? null,
                        period: periodByDsdId.get(cell.dsdId) ?? null,
                      }}
                    />
                  );
                }

                const blockKey = `${subIdx}-${blockIdx}`;
                return (
                  <DsdTableBlock
                    key={`table-${blockIdx}`}
                    focusedDsdId={
                      focusedBlockKey === blockKey ? focusedDsdId : null
                    }
                    setFocusedDsdId={setFocusedDsdId}
                    cells={block.cells}
                    counterpartDsdIds={counterpartDsdIds}
                    cornerSlotByDsdId={
                      positionSlotsByBlock?.slots.get(blockKey) ?? null
                    }
                    positionLineKeyByDsdId={
                      positionSlotsByBlock?.lineKeys.get(blockKey) ?? null
                    }
                    onPositionLineToggle={jumpToPositionLine}
                    positionHighlightDsdIds={
                      blockKey === positionHighlightBlockKey
                        ? (positionReview?.highlighted ?? null)
                        : null
                    }
                    dlViewIdByDsdId={dlViewIdByDsdId}
                    dlIndexByDsdId={dlIndexByDsdId}
                    dlTitleByDsdId={dlTitleByDsdId}
                    periodByDsdId={periodByDsdId}
                    lineItemIdByDsdId={lineItemIdByDsdId}
                    lineItemIndexByDsdId={lineItemIndexByDsdId}
                    lineItemTitleByDsdId={lineItemTitleByDsdId}
                    memberIdByDsdId={memberIdByDsdId}
                    memberIndexByDsdId={memberIndexByDsdId}
                    memberTitleByDsdId={memberTitleByDsdId}
                    onCellInteract={dispatchCellInteract}
                    tagPopoverDsdId={
                      popoverBlockKey === blockKey
                        ? (popover?.dsdId ?? null)
                        : null
                    }
                    onCellContextMenu={openTagPopover}
                  />
                );
              }
              if (block.blockType === "paragraph" && block.paragraphs) {
                return (
                  <DsdParagraphBlock
                    key={`paragraph-${blockIdx}`}
                    paragraphs={block.paragraphs}
                    sentenceMatches={block.sentenceMatches}
                    onValueClick={onValueClick}
                    onShouldTagCellClick={onShouldTagCellClick}
                  />
                );
              }
              return null;
            })}
          </div>
        ))}
      </div>

      <HeaderTagPopover
        containerRef={containerRef}
        dsdId={popover?.dsdId ?? null}
        kind={popover?.kind ?? null}
        tags={popover?.tags ?? null}
        onClose={() => setPopover(null)}
      />
    </div>
  );

  return (
    <PositionCurrentLineProvider value={positionCurrentLine}>
      {body}
    </PositionCurrentLineProvider>
  );
});
